import React, { useState } from 'react';
import { clsx } from 'clsx';

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: string | number;
  disabled?: boolean;
}

export type TabVariant = 'underline' | 'pills' | 'boxed' | 'soft';

export interface TabsProps {
  tabs: TabItem[];
  defaultTab?: string;
  activeTab?: string;
  onChange?: (tabId: string) => void;
  variant?: TabVariant;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  className?: string;
}

const variantStyles: Record<TabVariant, { container: string; tab: string; active: string }> = {
  underline: {
    container: 'border-b border-neutral-200',
    tab: 'px-4 py-3 text-neutral-600 hover:text-neutral-900 border-b-2 border-transparent -mb-px',
    active: 'text-primary-600 border-primary-600',
  },
  pills: {
    container: 'gap-2',
    tab: 'px-4 py-2 text-neutral-600 rounded-lg hover:bg-neutral-100',
    active: 'bg-primary-100 text-primary-700 hover:bg-primary-100',
  },
  boxed: {
    container: 'bg-neutral-100 p-1 rounded-lg gap-1',
    tab: 'px-4 py-2 text-neutral-600 rounded-md',
    active: 'bg-white text-neutral-900 shadow-sm',
  },
  soft: {
    container: 'gap-1',
    tab: 'px-4 py-2 text-neutral-600 rounded-lg hover:bg-neutral-50',
    active: 'bg-primary-50 text-primary-700',
  },
};

const sizeStyles: Record<string, string> = {
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base',
};

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  activeTab: controlledActiveTab,
  onChange,
  variant = 'underline',
  size = 'md',
  fullWidth = false,
  className,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id);
  const activeTab = controlledActiveTab ?? internalActiveTab;
  const styles = variantStyles[variant];

  const handleTabClick = (tabId: string) => {
    if (!controlledActiveTab) {
      setInternalActiveTab(tabId);
    }
    onChange?.(tabId);
  };

  return (
    <div
      role="tablist"
      className={clsx(
        'flex',
        styles.container,
        fullWidth && 'w-full',
        className
      )}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-disabled={tab.disabled}
          disabled={tab.disabled}
          onClick={() => !tab.disabled && handleTabClick(tab.id)}
          className={clsx(
            'inline-flex items-center gap-2 font-medium transition-all duration-200',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            sizeStyles[size],
            styles.tab,
            activeTab === tab.id && styles.active,
            fullWidth && 'flex-1 justify-center',
            tab.disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {tab.icon && <span className="w-5 h-5">{tab.icon}</span>}
          {tab.label}
          {tab.badge !== undefined && (
            <span
              className={clsx(
                'px-2 py-0.5 text-xs font-medium rounded-full',
                activeTab === tab.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-200 text-neutral-600'
              )}
            >
              {tab.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

Tabs.displayName = 'Tabs';

// Tab Panel Component
export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  tabId: string;
  activeTab: string;
}

export const TabPanel: React.FC<TabPanelProps> = ({
  tabId,
  activeTab,
  children,
  className,
  ...props
}) => {
  if (tabId !== activeTab) return null;

  return (
    <div
      role="tabpanel"
      aria-labelledby={tabId}
      className={clsx('animate-fade-in', className)}
      {...props}
    >
      {children}
    </div>
  );
};

TabPanel.displayName = 'TabPanel';

export default Tabs;

